import { X, Heart, MessageCircle, Share2, MapPin, Music, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Disc3, Play, Pause, Flag, User, Users, MapPinCheck, Trash2, Pencil, Check } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import ReportPostDialog from "@/components/ReportPostDialog";
import StreamingLinks from "@/components/StreamingLinks";
import CommentThread, { loadThreadedComments, type ThreadComment } from "@/components/CommentThread";
import { motion, AnimatePresence, useMotionValue, PanInfo } from "framer-motion";
import { useState, useRef, useEffect, useCallback } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useTrackView } from "@/hooks/useTrackView";
import { sfxLike, sfxUnlike } from "@/lib/sfx";
import { triggerPointsAnimation } from "@/components/PointsRewardAnimation";
import { useIsMobile } from "@/hooks/use-mobile";

interface VideoDetailModalProps {
  open: boolean;
  onClose: () => void;
  post: {
    id: string;
    artist: string;
    media_url: string;
    media_type: string;
    description?: string;
    venue?: string;
    concert_date?: string | null;
    song_name?: string;
    song_artist?: string;
    song_artwork_url?: string;
    song_preview_url?: string;
  };
  liked: boolean;
  likeCount: number;
  commentCount: number;
  displayName?: string;
  avatarUrl?: string;
  userId?: string;
  onLike: () => Promise<boolean>;
  onUnlike: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  hasNext?: boolean;
  hasPrev?: boolean;
  onDelete?: (postId: string) => void;
}

const SWIPE_ONBOARDING_KEY = "encore_swipe_onboarded";

const SwipeOnboarding = ({ onDismiss }: { onDismiss: () => void }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.3, delay: 0.5 }}
    className="absolute inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm"
    onClick={onDismiss}
  >
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.8, opacity: 0 }}
      transition={{ delay: 0.7, type: "spring", stiffness: 200, damping: 20 }}
      className="flex flex-col items-center gap-5 px-8 py-10 max-w-xs text-center"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="relative w-16 h-20">
        <motion.div
          animate={{ y: [0, -15, 0, 15, 0] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          className="absolute inset-0 flex items-center justify-center"
        >
          <div className="h-14 w-14 rounded-full bg-primary/20 border-2 border-primary/50 flex items-center justify-center">
            <ChevronUp className="h-5 w-5 text-primary" />
            <ChevronDown className="h-5 w-5 text-primary -mt-2" />
          </div>
        </motion.div>
      </div>

      <div className="space-y-2">
        <p className="font-display font-bold text-white text-lg">Swipe to Navigate</p>
        <p className="text-white/60 text-sm">↑↓ Next / Prev video</p>
        <p className="text-white/60 text-sm">← View profile</p>
        <p className="text-white/60 text-sm">→ Friends feed</p>
      </div>

      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={onDismiss}
        className="px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-display font-semibold text-sm shadow-lg shadow-primary/30"
      >
        Got it!
      </motion.button>
    </motion.div>
  </motion.div>
);

const VideoDetailModal = ({
  open, onClose, post, liked, likeCount, commentCount,
  displayName, avatarUrl, userId, onLike, onUnlike,
  onNext, onPrev, hasNext, hasPrev, onDelete,
}: VideoDetailModalProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const trackView = useTrackView();
  const isMobile = useIsMobile();
  const [comments, setComments] = useState<ThreadComment[]>([]);
  const [localLiked, setLocalLiked] = useState(liked);
  const [localLikeCount, setLocalLikeCount] = useState(likeCount);
  const [isLikeAnimating, setIsLikeAnimating] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [playingSong, setPlayingSong] = useState(false);
  const [videoPaused, setVideoPaused] = useState(true);
  const [videoProgress, setVideoProgress] = useState(0);
  const songAudioRef = useRef<HTMLAudioElement | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingVenue, setEditingVenue] = useState(false);
  const [editVenueValue, setEditVenueValue] = useState(post.venue || "");
  const dragX = useMotionValue(0);

  // "I Was There" state
  const [wasThereCount, setWasThereCount] = useState(0);
  const [iWasThere, setIWasThere] = useState(false);
  const [wasThereLoading, setWasThereLoading] = useState(false);

  // Load "I Was There" data
  useEffect(() => {
    if (!open) return;
    const load = async () => {
      const { count } = await supabase
        .from("post_was_there")
        .select("*", { count: "exact", head: true })
        .eq("post_id", post.id);
      setWasThereCount(count || 0);

      if (user) {
        const { data } = await supabase
          .from("post_was_there")
          .select("id")
          .eq("post_id", post.id)
          .eq("user_id", user.id)
          .limit(1);
        setIWasThere((data?.length || 0) > 0);
      }
    };
    load();
  }, [open, post.id, user]);

  const toggleWasThere = async () => {
    if (!user) { navigate("/auth"); return; }
    if (wasThereLoading) return;
    setWasThereLoading(true);
    try {
      if (iWasThere) {
        await supabase.from("post_was_there").delete().eq("post_id", post.id).eq("user_id", user.id);
        setIWasThere(false);
        setWasThereCount(c => Math.max(0, c - 1));
      } else {
        await supabase.from("post_was_there").insert({ post_id: post.id, user_id: user.id });
        setIWasThere(true);
        setWasThereCount(c => c + 1);
        toast.success("Marked as 'I Was There!' 🎤");
      }
    } catch {
      toast.error("Something went wrong");
    }
    setWasThereLoading(false);
  };

  // Show onboarding on first open on mobile
  useEffect(() => {
    if (open && isMobile) {
      const seen = localStorage.getItem(SWIPE_ONBOARDING_KEY);
      if (!seen) {
        setShowOnboarding(true);
      }
    }
  }, [open, isMobile]);

  const dismissOnboarding = () => {
    setShowOnboarding(false);
    localStorage.setItem(SWIPE_ONBOARDING_KEY, "true");
  };

  useEffect(() => {
    setLocalLiked(liked);
    setLocalLikeCount(likeCount);
  }, [liked, likeCount]);

  const loadComments = useCallback(async () => {
    const loaded = await loadThreadedComments(post.id);
    setComments(loaded);
  }, [post.id]);

  const openEventFromPost = useCallback(async () => {
    const artistQuery = (post.artist || post.song_artist || post.song_name || "").trim();
    if (!artistQuery) return;

    const normalizedVenue = (post.venue || "").toLowerCase();

    const pickMatchingEvent = (events: any[]) => {
      if (!normalizedVenue || events.length === 0) return events[0] || null;
      return (
        events.find((e: any) =>
          normalizedVenue.includes((e.venue || e.name || "").toLowerCase()) ||
          (e.venue || e.name || "").toLowerCase().includes(normalizedVenue.split(",")[0].trim())
        ) || events[0]
      );
    };

    try {
      const { data, error } = await supabase.functions.invoke("ticketmaster-search", {
        body: { keyword: artistQuery },
      });
      if (!error && data?.futureEvents?.length) {
        const matchedEvent = pickMatchingEvent(data.futureEvents);
        if (matchedEvent?.id) {
          onClose();
          navigate(`/event/${matchedEvent.id}`);
          return;
        }
      }
    } catch {}

    try {
      const { data, error } = await supabase.functions.invoke("setlistfm-search", {
        body: { artistName: artistQuery },
      });
      if (!error && data?.events?.length) {
        const matchedEvent = pickMatchingEvent(data.events);
        if (matchedEvent?.id) {
          onClose();
          navigate(`/event/${matchedEvent.id}`);
          return;
        }
      }
    } catch {}

    onClose();
    navigate(`/search?q=${encodeURIComponent(artistQuery)}&tab=shows`);
  }, [post.artist, post.song_artist, post.song_name, post.venue, navigate, onClose]);

  useEffect(() => {
    if (open) {
      loadComments();
      trackView(post.id);
      const v = videoRef.current;
      if (v) {
        v.currentTime = 0;
        v.play().catch(() => {});
      }
    } else {
      const v = videoRef.current;
      if (v) v.pause();
      if (songAudioRef.current) {
        songAudioRef.current.pause();
        songAudioRef.current = null;
        setPlayingSong(false);
      }
    }
  }, [open, post.id, loadComments, trackView]);

  const handleLike = async (e?: React.MouseEvent) => {
    if (!user) { navigate("/auth"); return; }
    setIsLikeAnimating(true);
    setTimeout(() => setIsLikeAnimating(false), 600);
    if (localLiked) {
      sfxUnlike();
      setLocalLiked(false);
      setLocalLikeCount(c => c - 1);
      onUnlike();
    } else {
      sfxLike();
      setLocalLiked(true);
      setLocalLikeCount(c => c + 1);
      const isFirst = await onLike();
      if (isFirst && e) triggerPointsAnimation(1, e.clientX, e.clientY);
    }
  };

  const toggleSongPreview = () => {
    if (!post.song_preview_url) return;
    if (playingSong && songAudioRef.current) {
      songAudioRef.current.pause();
      songAudioRef.current = null;
      setPlayingSong(false);
    } else {
      const audio = new Audio(post.song_preview_url);
      audio.volume = 0.4;
      audio.play().catch(() => {});
      audio.onended = () => setPlayingSong(false);
      songAudioRef.current = audio;
      setPlayingSong(true);
    }
  };

  const initials = (displayName || post.artist || "?").slice(0, 2).toUpperCase();
  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "now";
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  // Swipe handling for mobile
  const handleDragEnd = (_: any, info: PanInfo) => {
    const swipeThreshold = 80;
    const absX = Math.abs(info.offset.x);
    const absY = Math.abs(info.offset.y);

    if (absY > absX) {
      if (info.offset.y < -swipeThreshold && onNext && hasNext) {
        onNext();
      } else if (info.offset.y > swipeThreshold && onPrev && hasPrev) {
        onPrev();
      }
    } else {
      if (info.offset.x < -swipeThreshold && userId) {
        onClose();
        navigate(`/profile/${userId}`);
      } else if (info.offset.x > swipeThreshold) {
        onClose();
        navigate("/");
      }
    }
  };

  const deleteConfirmDialog = (
    <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this post?</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone. Your clip and all its likes, comments, and engagement data will be permanently removed.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={() => {
              onDelete?.(post.id);
              setShowDeleteConfirm(false);
              onClose();
            }}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  // ── MOBILE LAYOUT ──
  if (isMobile) {
    return (
      <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] bg-black flex flex-col"
          >
            {/* Swipe onboarding overlay */}
            <AnimatePresence>
              {showOnboarding && <SwipeOnboarding onDismiss={dismissOnboarding} />}
            </AnimatePresence>

            {/* Swipeable full-screen area */}
            <motion.div
              className="absolute inset-0"
              drag
              dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
              dragElastic={0.15}
              style={{ x: dragX }}
              onDragEnd={handleDragEnd}
            >
              {/* Media — fills entire screen */}
              <div className="absolute inset-0">
                {post.media_type === "video" ? (
                  <video
                    ref={videoRef}
                    src={post.media_url}
                    loop
                    playsInline
                    className="w-full h-full object-cover"
                    onTimeUpdate={() => {
                      const v = videoRef.current;
                      if (v && v.duration) setVideoProgress((v.currentTime / v.duration) * 100);
                    }}
                    onPlay={() => setVideoPaused(false)}
                    onPause={() => setVideoPaused(true)}
                    onClick={() => {
                      const v = videoRef.current;
                      if (v) { v.paused ? v.play() : v.pause(); }
                    }}
                  />
                ) : (
                  <img src={post.media_url} alt={post.artist} className="w-full h-full object-cover" />
                )}
              </div>

              {/* Gradients for readability */}
              <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/70 to-transparent pointer-events-none z-10" />
              <div className="absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-black/90 via-black/50 to-transparent pointer-events-none z-10" />

              {/* Top bar — close + progress */}
              <div className="absolute top-0 left-0 right-0 z-30 safe-area-top">
                <div className="flex items-center justify-between px-4 pt-3 pb-2">
                  <button
                    onClick={onClose}
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-black/40 backdrop-blur-md"
                  >
                    <X className="h-5 w-5 text-white" />
                  </button>
                  <div className="flex items-center gap-2" />
                </div>
                {post.media_type === "video" && (
                  <div className="h-0.5 bg-white/10 mx-4">
                    <div className="h-full bg-primary transition-[width] duration-150 ease-linear" style={{ width: `${videoProgress}%` }} />
                  </div>
                )}
              </div>

              {/* Play button overlay */}
              <AnimatePresence>
                {videoPaused && post.media_type === "video" && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="absolute inset-0 flex items-center justify-center pointer-events-none z-20"
                  >
                    <div className="h-16 w-16 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center">
                      <Play className="h-7 w-7 text-white ml-1" />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Like animation */}
              <AnimatePresence>
                {isLikeAnimating && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1.3, opacity: 1 }}
                    exit={{ scale: 1.6, opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="absolute inset-0 flex items-center justify-center pointer-events-none z-30"
                  >
                    <Heart className="h-24 w-24 fill-primary text-primary drop-shadow-2xl" />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Right-side action buttons (TikTok-style) */}
              <div className="absolute right-3 bottom-[220px] z-20 flex flex-col items-center gap-5">
                <button
                  onClick={(e) => handleLike(e)}
                  className="flex flex-col items-center gap-1"
                >
                  <div className="h-11 w-11 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
                    <Heart className={`h-6 w-6 transition-all ${localLiked ? "fill-primary text-primary" : "text-white"}`} />
                  </div>
                  <span className="text-[11px] font-display font-semibold text-white drop-shadow-md">{localLikeCount}</span>
                </button>

                <button
                  onClick={() => setShowComments(!showComments)}
                  className="flex flex-col items-center gap-1"
                >
                  <div className="h-11 w-11 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
                    <MessageCircle className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-[11px] font-display font-semibold text-white drop-shadow-md">{comments.length || commentCount}</span>
                </button>

                {/* I Was There button */}
                <button
                  onClick={toggleWasThere}
                  className="flex flex-col items-center gap-1"
                  disabled={wasThereLoading}
                >
                  <div className={`h-11 w-11 rounded-full backdrop-blur-sm flex items-center justify-center transition-all ${iWasThere ? "bg-emerald-500/40 ring-2 ring-emerald-400/60" : "bg-black/30"}`}>
                    <MapPinCheck className={`h-6 w-6 transition-all ${iWasThere ? "text-emerald-300" : "text-white"}`} />
                  </div>
                  {wasThereCount > 0 && (
                    <span className="text-[11px] font-display font-semibold text-white drop-shadow-md">{wasThereCount}</span>
                  )}
                </button>

                <button
                  onClick={async () => {
                    const shareUrl = `${window.location.origin}/post/${post.id}`;
                    if (navigator.share) {
                      try { await navigator.share({ title: `${post.artist} at ${post.venue}`, url: shareUrl }); return; } catch {}
                    }
                    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied!"); } catch { toast.error("Couldn't share"); }
                  }}
                  className="flex flex-col items-center gap-1"
                >
                  <div className="h-11 w-11 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
                    <Share2 className="h-5 w-5 text-white" />
                  </div>
                </button>

                {/* Delete (own post only) */}
                {onDelete && user?.id === userId && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="flex flex-col items-center gap-1"
                  >
                    <div className="h-11 w-11 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
                      <Trash2 className="h-5 w-5 text-destructive" />
                    </div>
                  </button>
                )}
              </div>
              <div className="absolute bottom-0 left-0 right-14 z-20 px-4 pb-6 safe-area-bottom">
                {/* User info */}
                <div
                  className="flex items-center gap-2.5 mb-3 cursor-pointer"
                  onClick={() => { if (userId) { onClose(); navigate(`/profile/${userId}`); } }}
                >
                  <Avatar className="h-10 w-10 ring-2 ring-white/30">
                    {avatarUrl && <AvatarImage src={avatarUrl} />}
                    <AvatarFallback className="text-xs font-display bg-white/10 text-white">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-bold text-white text-sm drop-shadow-md">@{displayName || "fan"}</p>
                    {post.venue && (
                      <p className="text-[11px] text-white/70 truncate drop-shadow-sm flex items-center gap-1">
                        <MapPin className="h-2.5 w-2.5 shrink-0" /> {post.venue}
                      </p>
                    )}
                  </div>
                </div>

                {/* "I Was There" badge */}
                {wasThereCount > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-1.5 mb-2"
                  >
                    <div className="flex items-center gap-1 rounded-full bg-emerald-500/20 border border-emerald-400/30 px-2.5 py-1">
                      <MapPinCheck className="h-3 w-3 text-emerald-400" />
                      <span className="text-[10px] font-display font-bold text-emerald-300">
                        {wasThereCount} {wasThereCount === 1 ? "fan" : "fans"} were there
                      </span>
                    </div>
                  </motion.div>
                )}

                {/* Description */}
                {post.description && (
                  <p className="text-xs text-white/80 leading-relaxed mb-3 line-clamp-2 drop-shadow-sm">{post.description}</p>
                )}

                {/* Song / Artist card */}
                {post.song_name ? (
                  <div
                    onClick={openEventFromPost}
                    className="flex items-center gap-2.5 bg-white/10 backdrop-blur-md rounded-xl px-3 py-2.5 cursor-pointer"
                  >
                    {post.song_artwork_url ? (
                      <img src={post.song_artwork_url} alt="" className="h-9 w-9 rounded-lg object-cover shrink-0" />
                    ) : (
                      <div className="h-9 w-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                        <Disc3 className="h-4 w-4 text-white" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-white font-display font-semibold text-xs truncate flex items-center gap-1">
                        <Disc3 className="h-3 w-3 text-primary shrink-0" />
                        {post.song_name}
                      </p>
                      <p className="text-white/60 text-[10px] truncate">{post.song_artist}</p>
                    </div>
                    {post.song_preview_url && (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleSongPreview(); }}
                        className="h-7 w-7 rounded-full bg-white/10 flex items-center justify-center shrink-0"
                      >
                        {playingSong ? <Pause className="h-3 w-3 text-white" /> : <Play className="h-3 w-3 text-white ml-0.5" />}
                      </button>
                    )}
                  </div>
                ) : post.artist ? (
                  <div
                    onClick={openEventFromPost}
                    className="flex items-center gap-2.5 bg-white/10 backdrop-blur-md rounded-xl px-3 py-2.5 cursor-pointer"
                  >
                    <div className="h-8 w-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                      <Music className="h-4 w-4 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-white font-display font-semibold text-xs truncate">{post.artist}</p>
                      <p className="text-white/60 text-[10px] truncate">{post.venue || "Live Performance"}</p>
                    </div>
                  </div>
                ) : null}

                {/* Swipe hints */}
                <div className="flex items-center justify-center gap-4 mt-3">
                  {hasPrev && (
                    <motion.div
                      animate={{ y: [3, 0, 3] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="flex items-center gap-1 text-[10px] text-white/40 font-display"
                    >
                      <ChevronDown className="h-3 w-3" /> Prev
                    </motion.div>
                  )}
                  {hasNext && (
                    <motion.div
                      animate={{ y: [-3, 0, -3] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="flex items-center gap-1 text-[10px] text-white/40 font-display"
                    >
                      <ChevronUp className="h-3 w-3" /> Next
                    </motion.div>
                  )}
                  <motion.div
                    animate={{ x: [-3, 0, -3] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="flex items-center gap-1 text-[10px] text-white/40 font-display"
                  >
                    <User className="h-3 w-3" /> ←
                  </motion.div>
                  <motion.div
                    animate={{ x: [3, 0, 3] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="flex items-center gap-1 text-[10px] text-white/40 font-display"
                  >
                    → <Users className="h-3 w-3" />
                  </motion.div>
                </div>
              </div>
            </motion.div>

            {/* Comments bottom sheet */}
            <AnimatePresence>
              {showComments && (
                <motion.div
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "100%" }}
                  transition={{ type: "spring", damping: 25, stiffness: 300 }}
                  className="absolute bottom-0 left-0 right-0 z-[120] max-h-[55vh] bg-card rounded-t-2xl border-t border-border/50 flex flex-col"
                >
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border/30">
                    <h3 className="font-display font-bold text-foreground text-base">Comments</h3>
                    <button onClick={() => setShowComments(false)} className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center">
                      <ChevronDown className="h-4 w-4 text-foreground" />
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    <CommentThread postId={post.id} comments={comments} setComments={setComments} postOwnerId={userId} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
      {deleteConfirmDialog}
    </>
    );
  }

  // ── DESKTOP LAYOUT ──
  return (
    <>
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] bg-black flex"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-5 left-5 z-30 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 backdrop-blur-md hover:bg-black/70 transition-colors"
          >
            <X className="h-5 w-5 text-white" />
          </button>

          {/* Prev / Next navigation */}
          {onPrev && hasPrev && (
            <button
              onClick={onPrev}
              className="absolute left-5 top-1/2 -translate-y-1/2 z-30 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 backdrop-blur-md hover:bg-black/70 transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-white" />
            </button>
          )}
          {onNext && (
            hasNext ? (
              <button
                onClick={onNext}
                className="absolute right-5 top-1/2 -translate-y-1/2 z-30 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 backdrop-blur-md hover:bg-black/70 transition-colors"
              >
                <ChevronRight className="h-5 w-5 text-white" />
              </button>
            ) : (
              <div className="absolute right-5 top-1/2 -translate-y-1/2 z-30 flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-black/50 backdrop-blur-md">
                <span className="text-sm text-white/80 font-display font-medium">You've reached the end 🎤</span>
              </div>
            )
          )}

          {/* Like animation overlay */}
          <AnimatePresence>
            {isLikeAnimating && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1.3, opacity: 1 }}
                exit={{ scale: 1.6, opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0 flex items-center justify-center pointer-events-none z-30"
              >
                <Heart className="h-24 w-24 fill-primary text-primary drop-shadow-2xl" />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Media area */}
          <div
            className="flex-1 min-w-0 flex items-center justify-center bg-black relative group cursor-pointer overflow-hidden"
            onClick={() => {
              const v = videoRef.current;
              if (v) { v.paused ? v.play() : v.pause(); setVideoPaused(v.paused); }
            }}
          >
            {post.media_type === "video" ? (
              <>
                <video
                  ref={videoRef}
                  src={post.media_url}
                  loop
                  playsInline
                  className="w-full h-full object-contain"
                  onTimeUpdate={() => {
                    const v = videoRef.current;
                    if (v && v.duration) setVideoProgress((v.currentTime / v.duration) * 100);
                  }}
                  onPlay={() => setVideoPaused(false)}
                  onPause={() => setVideoPaused(true)}
                />
                <AnimatePresence>
                  {videoPaused && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      className="absolute inset-0 flex items-center justify-center pointer-events-none"
                    >
                      <div className="h-16 w-16 rounded-full bg-black/50 backdrop-blur-md flex items-center justify-center">
                        <Play className="h-7 w-7 text-white ml-1" />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
                  <div className="h-full bg-primary transition-[width] duration-150 ease-linear" style={{ width: `${videoProgress}%` }} />
                </div>
              </>
            ) : (
              <img src={post.media_url} alt={post.artist} className="w-full h-full object-contain" />
            )}
          </div>

          {/* Info panel — desktop */}
          <motion.div
            initial={{ x: 60, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 60, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="w-[360px] max-w-[85vw] h-full flex-shrink-0 border-l border-border/50 bg-card flex flex-col"
          >
            <div className="flex-1 overflow-y-auto p-5 min-w-0 space-y-5 overflow-x-hidden">
              {/* Artist profile */}
              <div
                className="flex min-w-0 items-center gap-3.5 cursor-pointer group"
                onClick={() => { if (userId) { onClose(); navigate(`/profile/${userId}`); } }}
              >
                <Avatar className="h-14 w-14 ring-2 ring-primary/50 shadow-lg shadow-primary/20 group-hover:ring-primary transition-all">
                  {avatarUrl && <AvatarImage src={avatarUrl} />}
                  <AvatarFallback className="text-sm font-display bg-secondary text-foreground">{initials}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1 overflow-hidden">
                  <p className="font-display font-bold text-foreground text-base truncate">@{displayName || "fan"}</p>
                  {editingVenue ? (
                    <div className="flex items-center gap-1.5 mt-0.5" onClick={(e) => e.stopPropagation()}>
                      <MapPin className="h-3 w-3 shrink-0 text-primary" />
                      <input
                        autoFocus
                        className="bg-secondary border border-border rounded px-2 py-0.5 text-xs text-foreground w-full max-w-[200px]"
                        value={editVenueValue}
                        onChange={(e) => setEditVenueValue(e.target.value)}
                        onKeyDown={async (e) => {
                          if (e.key === "Enter") {
                            await supabase.from("posts").update({ venue: editVenueValue } as any).eq("id", post.id);
                            post.venue = editVenueValue;
                            setEditingVenue(false);
                            toast.success("Venue updated");
                          }
                          if (e.key === "Escape") setEditingVenue(false);
                        }}
                      />
                      <button
                        onClick={async () => {
                          await supabase.from("posts").update({ venue: editVenueValue } as any).eq("id", post.id);
                          post.venue = editVenueValue;
                          setEditingVenue(false);
                          toast.success("Venue updated");
                        }}
                        className="text-primary hover:text-primary/80"
                      >
                        <Check className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : post.venue ? (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5 truncate">
                      <div
                        className="flex items-center gap-1 cursor-pointer hover:text-primary transition-colors truncate"
                        onClick={(e) => { e.stopPropagation(); onClose(); navigate(`/search?q=${encodeURIComponent(post.artist)}&tab=shows`); }}
                      >
                        <MapPin className="h-3 w-3 shrink-0" /> <span className="truncate">{post.venue}</span>
                      </div>
                      {user?.id === userId && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditVenueValue(post.venue || ""); setEditingVenue(true); }}
                          className="text-muted-foreground hover:text-primary transition-colors shrink-0"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>

              {/* "I Was There" badge */}
              {wasThereCount > 0 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center gap-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 px-3 py-2"
                >
                  <MapPinCheck className="h-4 w-4 text-emerald-500 shrink-0" />
                  <span className="text-xs font-display font-semibold text-emerald-600 dark:text-emerald-400">
                    {wasThereCount} {wasThereCount === 1 ? "fan was" : "fans were"} there
                  </span>
                </motion.div>
              )}

              {/* Song info */}
              {post.song_name ? (
                <div
                  onClick={openEventFromPost}
                  className="flex items-center gap-3 bg-secondary/50 rounded-xl px-4 py-3 cursor-pointer hover:bg-secondary/80 transition-colors"
                >
                  {post.song_artwork_url ? (
                    <img src={post.song_artwork_url} alt="" className="h-11 w-11 rounded-lg object-cover shadow-sm shrink-0" />
                  ) : (
                    <div className="h-11 w-11 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
                      <Disc3 className="h-5 w-5 text-primary" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-foreground font-display font-semibold text-sm truncate flex items-center gap-1.5">
                      <Disc3 className="h-3 w-3 text-primary shrink-0" />
                      {post.song_name}
                    </p>
                    <p className="text-muted-foreground text-xs truncate">{post.song_artist}</p>
                  </div>
                  {post.song_preview_url && (
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSongPreview(); }}
                      className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center hover:bg-secondary/80 transition-colors shrink-0"
                    >
                      {playingSong ? <Pause className="h-3.5 w-3.5 text-foreground" /> : <Play className="h-3.5 w-3.5 text-foreground ml-0.5" />}
                    </button>
                  )}
                </div>
              ) : post.artist ? (
                <div
                  onClick={openEventFromPost}
                  className="flex items-center gap-3 bg-secondary/50 rounded-xl px-4 py-3 cursor-pointer hover:bg-secondary/80 transition-colors"
                >
                  <div className="h-10 w-10 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
                    <Music className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-foreground font-display font-semibold text-sm truncate">{post.artist}</p>
                    <p className="text-muted-foreground text-xs truncate">{post.venue || "Live Performance"}</p>
                  </div>
                </div>
              ) : null}

              {post.song_name && post.song_artist && (
                <div className="min-w-0 overflow-x-hidden">
                  <StreamingLinks songName={post.song_name} artistName={post.song_artist} />
                </div>
              )}

              {post.description && (
                <p className="text-sm text-muted-foreground leading-relaxed break-words">{post.description}</p>
              )}

              {/* Likes, Comments, I Was There & actions */}
              <div className="flex min-w-0 items-center gap-1.5 flex-wrap overflow-visible">
                <motion.button
                  whileTap={{ scale: 0.8 }}
                  onClick={handleLike}
                  className="flex shrink-0 items-center gap-1.5 rounded-full bg-secondary/50 px-2.5 py-2 hover:bg-secondary transition-colors"
                >
                  <Heart className={`h-5 w-5 transition-all ${localLiked ? "fill-primary text-primary" : "text-foreground"}`} />
                  <span className="text-sm font-display font-semibold text-foreground">{localLikeCount}</span>
                </motion.button>

                <motion.button
                  whileTap={{ scale: 0.8 }}
                  onClick={() => setShowComments(!showComments)}
                  className="flex shrink-0 items-center gap-1.5 rounded-full bg-secondary/50 px-2.5 py-2 hover:bg-secondary transition-colors"
                >
                  <MessageCircle className="h-5 w-5 text-foreground" />
                  <span className="text-sm font-display font-semibold text-foreground">{comments.length || commentCount}</span>
                </motion.button>

                {/* I Was There button */}
                <motion.button
                  whileTap={{ scale: 0.8 }}
                  onClick={toggleWasThere}
                  disabled={wasThereLoading}
                  className={`flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-2 transition-colors ${
                    iWasThere
                      ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/30"
                      : "bg-secondary/50 hover:bg-secondary"
                  }`}
                >
                  <MapPinCheck className={`h-5 w-5 ${iWasThere ? "text-emerald-500" : "text-foreground"}`} />
                  <span className={`text-sm font-display font-semibold ${iWasThere ? "" : "text-foreground"}`}>
                    {iWasThere ? "Was There" : "I Was There"}
                  </span>
                </motion.button>

                <motion.button
                  whileTap={{ scale: 0.8 }}
                  className="flex shrink-0 items-center gap-1.5 rounded-full bg-secondary/50 px-2.5 py-2 hover:bg-secondary transition-colors"
                  onClick={async () => {
                    const shareUrl = `${window.location.origin}/post/${post.id}`;
                    const shareData = { title: `${post.artist} at ${post.venue}`, url: shareUrl };
                    if (navigator.share) {
                      try { await navigator.share(shareData); return; } catch {}
                    }
                    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied!"); } catch { toast.error("Couldn't share right now."); }
                  }}
                >
                  <Share2 className="h-5 w-5 text-foreground" />
                </motion.button>

                <ReportPostDialog
                  postId={post.id}
                  trigger={
                    <button className="flex shrink-0 items-center gap-1.5 rounded-full bg-secondary/50 px-2.5 py-2 hover:bg-destructive/20 active:scale-[0.8] transition-all">
                      <Flag className="h-5 w-5 text-muted-foreground" />
                    </button>
                  }
                />

                {/* Delete (own post only) */}
                {onDelete && user?.id === userId && (
                  <motion.button
                    whileTap={{ scale: 0.8 }}
                    onClick={() => setShowDeleteConfirm(true)}
                    className="flex shrink-0 items-center gap-1.5 rounded-full bg-destructive/10 px-2.5 py-2 hover:bg-destructive/20 transition-colors"
                  >
                    <Trash2 className="h-5 w-5 text-destructive" />
                  </motion.button>
                )}
              </div>

              {/* Comments section */}
              <AnimatePresence>
                {showComments && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="bg-secondary/30 rounded-xl border border-border/50 overflow-hidden">
                      <CommentThread postId={post.id} comments={comments} setComments={setComments} postOwnerId={userId} />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
      {deleteConfirmDialog}
    </>
  );
};

export default VideoDetailModal;
